import React, { useEffect, useState, CSSProperties } from "react";
import axios from "axios";
import QRCode from "qrcode";
import { detailcommande } from "../../Modeles/Commande";

interface PayArticleModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: detailcommande | null;
  buyerType?: "magasin" | "commercial" | "client" | "visiteur";
  buyerId?: string | number | null;
  buyerName?: string | null;
  onSuccess: (newStatut: string, newSolde?: number) => void;
}

interface EnCompteInfo {
  en_compte: boolean;
  solde: number;
  entry_id: number | null;
  seller_id?: number | null;
  seller_name?: string | null;
}

export default function PayArticleModal({
  isOpen,
  onClose,
  item,
  buyerType = "magasin",
  buyerId,
  buyerName,
  onSuccess,
}: PayArticleModalProps) {
  const [loadingCheck, setLoadingCheck] = useState<boolean>(true);
  const [enCompteInfo, setEnCompteInfo] = useState<EnCompteInfo | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<"solde" | "rib">("rib");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [copied, setCopied] = useState<boolean>(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>("");

  const effectiveBuyerId =
    buyerId ||
    (buyerType === "magasin"
      ? localStorage.getItem("MagasinId")
      : buyerType === "commercial"
      ? localStorage.getItem("CommercialId") || localStorage.getItem("id")
      : localStorage.getItem("ClientId") || localStorage.getItem("VisiteurId"));

  const effectiveBuyerName =
    buyerName ||
    (buyerType === "magasin"
      ? localStorage.getItem("MagasinName")
      : buyerType === "commercial"
      ? localStorage.getItem("CommercialName") || localStorage.getItem("nom")
      : localStorage.getItem("ClientName") || localStorage.getItem("VisiteurName") || "Acheteur");

  const totalAmount = item
    ? Math.round(Number(item.prix || 0) * Number(item.quantity || 1) * 100) / 100
    : 0;

  useEffect(() => {
    if (!item) return;

    // Structured payment info for QR code
    const qrData = `RIB: 007 780 0003102000302865 77\nBENEFICIAIRE: ${item.vendeur || "WYN Commerce"}\nMONTANT: ${totalAmount} MAD\nREF: CMD#${item.idCommande || item.id} - ${item.RefArticle}`;

    QRCode.toDataURL(qrData, {
      width: 160,
      margin: 1,
      color: {
        dark: "#0f172a",
        light: "#ffffff",
      },
    })
      .then((url) => setQrCodeUrl(url))
      .catch((err) => console.error("Erreur génération QR Code", err));
  }, [item, totalAmount]);

  useEffect(() => {
    if (!isOpen || !item) return;

    setErrorMessage("");
    setLoadingCheck(true);

    const checkEnCompte = async () => {
      try {
        const seller = item.vendeur || "";
        const url = `${process.env.REACT_APP_PHP_APP_URL}/users/${buyerType}/${effectiveBuyerId}/check-en-compte`;
        const res = await axios.get(url, { params: { seller_name: seller } });
        const data: EnCompteInfo = res.data;
        setEnCompteInfo(data);

        if (data.en_compte && data.solde >= totalAmount) {
          setPaymentMethod("solde");
        } else {
          setPaymentMethod("rib");
        }
      } catch (err) {
        setEnCompteInfo({
          en_compte: false,
          solde: 0,
          entry_id: null,
          seller_name: item.vendeur,
        });
        setPaymentMethod("rib");
      } finally {
        setLoadingCheck(false);
      }
    };

    checkEnCompte();
  }, [isOpen, item, buyerType, effectiveBuyerId, totalAmount]);

  if (!isOpen || !item) return null;

  const handleCopyRIB = () => {
    navigator.clipboard.writeText("007 780 0003102000302865 77");
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleConfirmPayment = async () => {
    if (!item) return;
    setErrorMessage("");
    setSubmitting(true);

    try {
      const payload = {
        payment_method: paymentMethod,
        buyer_type: buyerType,
        buyer_id: effectiveBuyerId,
        seller_name: item.vendeur,
        entry_id: enCompteInfo?.entry_id,
        amount: totalAmount,
      };

      const res = await axios.post(
        `${process.env.REACT_APP_PHP_APP_URL}/commandes/detail/${item.id}/pay`,
        payload
      );

      const newStatut = res.data.new_statut || "payé par magasin";
      const newSolde = res.data.new_solde;

      onSuccess(newStatut, newSolde);
      onClose();
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        "Une erreur est survenue lors de l'enregistrement du paiement.";
      setErrorMessage(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const isSoldeAvailable = !!(
    enCompteInfo?.en_compte && (enCompteInfo.solde || 0) >= totalAmount
  );

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(15, 23, 42, 0.65)",
        backdropFilter: "blur(5px)",
        zIndex: 10000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "20px",
          width: "100%",
          maxWidth: "520px",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          overflow: "hidden",
          border: "1px solid #e2e8f0",
          animation: "scaleIn 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        <style>{`
          @keyframes scaleIn {
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
          }
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>

        {/* Modal Header */}
        <div
          style={{
            padding: "20px 24px",
            background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
            color: "#ffffff",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "12px",
                backgroundColor: "rgba(255, 196, 0, 0.2)",
                color: "#ffc400",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "20px",
              }}
            >
              💳
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: "17px", fontWeight: 700 }}>
                Paiement de l'article
              </h3>
              <p
                style={{
                  margin: "2px 0 0 0",
                  fontSize: "12px",
                  color: "#94a3b8",
                }}
              >
                Acheteur : <strong>{effectiveBuyerName}</strong>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            style={{
              background: "transparent",
              border: "none",
              color: "#94a3b8",
              fontSize: "22px",
              cursor: submitting ? "not-allowed" : "pointer",
              lineHeight: 1,
              padding: "4px",
            }}
          >
            ✕
          </button>
        </div>

        {/* Modal Body */}
        <div style={{ padding: "22px 24px", maxHeight: "75vh", overflowY: "auto" }}>
          {/* Article Summary Card */}
          <div
            style={{
              backgroundColor: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "14px",
              padding: "16px",
              marginBottom: "18px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "10px",
              }}
            >
              <div>
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    color: "#64748b",
                  }}
                >
                  Réf: {item.RefArticle}
                </span>
                <h4
                  style={{
                    margin: "2px 0 0 0",
                    fontSize: "15px",
                    fontWeight: 600,
                    color: "#0f172a",
                  }}
                >
                  {item.NomArticle}
                </h4>
              </div>
              <span
                style={{
                  backgroundColor: "#eff6ff",
                  color: "#2563eb",
                  border: "1px solid #bfdbfe",
                  padding: "3px 10px",
                  borderRadius: "20px",
                  fontSize: "12px",
                  fontWeight: 600,
                }}
              >
                🏪 Vendeur: {item.vendeur}
              </span>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: "10px",
                paddingTop: "10px",
                borderTop: "1px dashed #cbd5e1",
              }}
            >
              <div>
                <div style={{ fontSize: "11px", color: "#64748b" }}>Prix unit.</div>
                <div style={{ fontSize: "13px", fontWeight: 600, color: "#334155" }}>
                  {item.prix} MAD
                </div>
              </div>
              <div>
                <div style={{ fontSize: "11px", color: "#64748b" }}>Quantité</div>
                <div style={{ fontSize: "13px", fontWeight: 600, color: "#334155" }}>
                  {item.quantity}
                </div>
              </div>
              <div>
                <div style={{ fontSize: "11px", color: "#64748b" }}>Total à payer</div>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: 700,
                    color: "#059669",
                  }}
                >
                  {totalAmount} MAD
                </div>
              </div>
            </div>
          </div>

          {/* En Compte status indicator */}
          {loadingCheck ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                padding: "12px 16px",
                backgroundColor: "#f1f5f9",
                borderRadius: "10px",
                marginBottom: "18px",
                fontSize: "13px",
                color: "#64748b",
              }}
            >
              <div
                style={{
                  width: "18px",
                  height: "18px",
                  border: "2px solid #cbd5e1",
                  borderTop: "2px solid #ffc400",
                  borderRadius: "50%",
                  animation: "spin 0.8s linear infinite",
                }}
              />
              Vérification de la relation en compte avec {item.vendeur}…
            </div>
          ) : enCompteInfo?.en_compte ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 14px",
                backgroundColor: "#ecfdf5",
                border: "1px solid #a7f3d0",
                borderRadius: "10px",
                marginBottom: "18px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontSize: "16px" }}>✅</span>
                <span style={{ fontSize: "13px", fontWeight: 600, color: "#065f46" }}>
                  En compte avec {item.vendeur}
                </span>
              </div>
              <div style={{ fontSize: "13px", color: "#047857" }}>
                Solde dispo : <strong>{enCompteInfo.solde} MAD</strong>
              </div>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 14px",
                backgroundColor: "#fffbeb",
                border: "1px solid #fde68a",
                borderRadius: "10px",
                marginBottom: "18px",
                fontSize: "13px",
                color: "#92400e",
              }}
            >
              <span>ℹ️</span>
              <span>
                Non en compte avec <strong>{item.vendeur}</strong>. Paiement direct par RIB uniquement.
              </span>
            </div>
          )}

          {/* Payment Method Selector */}
          <div style={{ marginBottom: "18px" }}>
            <label
              style={{
                display: "block",
                fontSize: "12px",
                fontWeight: 700,
                color: "#334155",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                marginBottom: "10px",
              }}
            >
              Méthode de paiement :
            </label>

            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {/* Option 1: SOLDE (Only visible if en_compte is true) */}
              {enCompteInfo?.en_compte && (
                <div
                  onClick={() => {
                    if (isSoldeAvailable) setPaymentMethod("solde");
                  }}
                  style={{
                    border: `2px solid ${
                      paymentMethod === "solde" ? "#059669" : "#e2e8f0"
                    }`,
                    backgroundColor:
                      paymentMethod === "solde" ? "#f0fdf4" : isSoldeAvailable ? "#ffffff" : "#f8fafc",
                    borderRadius: "12px",
                    padding: "14px 16px",
                    cursor: isSoldeAvailable ? "pointer" : "not-allowed",
                    opacity: isSoldeAvailable ? 1 : 0.6,
                    transition: "all 0.2s ease",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <input
                        type="radio"
                        name="payMethod"
                        checked={paymentMethod === "solde"}
                        onChange={() => {
                          if (isSoldeAvailable) setPaymentMethod("solde");
                        }}
                        disabled={!isSoldeAvailable}
                        style={{ accentColor: "#059669", width: "18px", height: "18px" }}
                      />
                      <div>
                        <div style={{ fontSize: "14px", fontWeight: 700, color: "#0f172a" }}>
                          💰 Payer avec Solde (En Compte)
                        </div>
                        <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                          Déduction automatique du solde chez {item.vendeur}
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <span
                        style={{
                          fontSize: "13px",
                          fontWeight: 700,
                          color: isSoldeAvailable ? "#059669" : "#dc2626",
                        }}
                      >
                        {enCompteInfo.solde} MAD
                      </span>
                    </div>
                  </div>

                  {!isSoldeAvailable && (
                    <div
                      style={{
                        marginTop: "8px",
                        fontSize: "11px",
                        color: "#dc2626",
                        fontWeight: 600,
                      }}
                    >
                      ⚠️ Solde insuffisant ({enCompteInfo.solde} MAD disponible, requis : {totalAmount} MAD)
                    </div>
                  )}

                  {paymentMethod === "solde" && isSoldeAvailable && (
                    <div
                      style={{
                        marginTop: "10px",
                        paddingTop: "8px",
                        borderTop: "1px dashed #bbf7d0",
                        fontSize: "12px",
                        color: "#065f46",
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <span>Solde restant après paiement :</span>
                      <strong>
                        {Math.round((enCompteInfo.solde - totalAmount) * 100) / 100} MAD
                      </strong>
                    </div>
                  )}
                </div>
              )}

              {/* Option 2: RIB */}
              <div
                onClick={() => setPaymentMethod("rib")}
                style={{
                  border: `2px solid ${
                    paymentMethod === "rib" ? "#2563eb" : "#e2e8f0"
                  }`,
                  backgroundColor: paymentMethod === "rib" ? "#eff6ff" : "#ffffff",
                  borderRadius: "12px",
                  padding: "14px 16px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <input
                      type="radio"
                      name="payMethod"
                      checked={paymentMethod === "rib"}
                      onChange={() => setPaymentMethod("rib")}
                      style={{ accentColor: "#2563eb", width: "18px", height: "18px" }}
                    />
                    <div>
                      <div style={{ fontSize: "14px", fontWeight: 700, color: "#0f172a" }}>
                        🏦 Payer avec RIB (Virement bancaire)
                      </div>
                      <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                        Virement sur le compte bancaire officiel
                      </div>
                    </div>
                  </div>
                </div>

                {paymentMethod === "rib" && (
                  <div
                    style={{
                      marginTop: "12px",
                      paddingTop: "12px",
                      borderTop: "1px dashed #bfdbfe",
                    }}
                  >
                    {/* RIB details & Copy button */}
                    <div
                      style={{
                        backgroundColor: "#ffffff",
                        border: "1px solid #bfdbfe",
                        borderRadius: "8px",
                        padding: "10px 12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: "12px",
                      }}
                    >
                      <div>
                        <div style={{ fontSize: "11px", color: "#64748b" }}>
                          RIB Bancaire :
                        </div>
                        <div
                          style={{
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: "13px",
                            fontWeight: 700,
                            color: "#1e293b",
                            letterSpacing: "0.5px",
                          }}
                        >
                          007 780 0003102000302865 77
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleCopyRIB}
                        style={{
                          backgroundColor: copied ? "#dcfce7" : "#f1f5f9",
                          color: copied ? "#166534" : "#334155",
                          border: `1px solid ${copied ? "#86efac" : "#cbd5e1"}`,
                          borderRadius: "6px",
                          padding: "6px 10px",
                          fontSize: "12px",
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        {copied ? "✓ Copié" : "Copier"}
                      </button>
                    </div>

                    {/* QR Code Section */}
                    {qrCodeUrl && (
                      <div
                        style={{
                          backgroundColor: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          borderRadius: "10px",
                          padding: "12px",
                          display: "flex",
                          alignItems: "center",
                          gap: "14px",
                          marginBottom: "10px",
                        }}
                      >
                        <div
                          style={{
                            backgroundColor: "#ffffff",
                            padding: "6px",
                            borderRadius: "8px",
                            border: "1px solid #cbd5e1",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                            flexShrink: 0,
                          }}
                        >
                          <img
                            src={qrCodeUrl}
                            alt="QR Code de paiement RIB"
                            style={{
                              width: "110px",
                              height: "110px",
                              display: "block",
                              borderRadius: "4px",
                            }}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                              fontSize: "11px",
                              fontWeight: 700,
                              color: "#2563eb",
                              backgroundColor: "#eff6ff",
                              border: "1px solid #bfdbfe",
                              padding: "2px 8px",
                              borderRadius: "12px",
                              marginBottom: "4px",
                            }}
                          >
                            📱 Scanner pour payer
                          </div>
                          <div
                            style={{
                              fontSize: "12px",
                              fontWeight: 600,
                              color: "#1e293b",
                              marginBottom: "2px",
                            }}
                          >
                            Application Bancaire Mobile
                          </div>
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#64748b",
                              lineHeight: "1.35",
                            }}
                          >
                            Scannez ce QR Code avec votre appli bancaire pour récupérer les infos de virement.
                          </div>
                          <div
                            style={{
                              marginTop: "4px",
                              fontSize: "11px",
                              fontWeight: 700,
                              color: "#059669",
                            }}
                          >
                            Montant : {totalAmount} MAD
                          </div>
                        </div>
                      </div>
                    )}

                    <div
                      style={{
                        marginTop: "8px",
                        fontSize: "11px",
                        color: "#475569",
                        lineHeight: "1.4",
                      }}
                    >
                      💡 Mentionnez la référence <strong>#{item.idCommande || item.id} ({item.RefArticle})</strong> dans le libellé de votre virement.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Error Message */}
          {errorMessage && (
            <div
              style={{
                backgroundColor: "#fef2f2",
                border: "1px solid #fecaca",
                color: "#dc2626",
                borderRadius: "10px",
                padding: "10px 14px",
                marginBottom: "16px",
                fontSize: "13px",
                fontWeight: 500,
              }}
            >
              ✕ {errorMessage}
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              style={{
                flex: 1,
                padding: "12px",
                borderRadius: "10px",
                border: "1px solid #cbd5e1",
                backgroundColor: "#ffffff",
                color: "#475569",
                fontWeight: 600,
                fontSize: "14px",
                cursor: submitting ? "not-allowed" : "pointer",
              }}
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleConfirmPayment}
              disabled={submitting || (paymentMethod === "solde" && !isSoldeAvailable)}
              style={{
                flex: 2,
                padding: "12px",
                borderRadius: "10px",
                border: "none",
                background:
                  paymentMethod === "solde"
                    ? "linear-gradient(135deg, #059669 0%, #10b981 100%)"
                    : "linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)",
                color: "#ffffff",
                fontWeight: 700,
                fontSize: "14px",
                cursor:
                  submitting || (paymentMethod === "solde" && !isSoldeAvailable)
                    ? "not-allowed"
                    : "pointer",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting ? (
                <>
                  <div
                    style={{
                      width: "16px",
                      height: "16px",
                      border: "2px solid rgba(255,255,255,0.4)",
                      borderTop: "2px solid #fff",
                      borderRadius: "50%",
                      animation: "spin 0.8s linear infinite",
                    }}
                  />
                  Traitement…
                </>
              ) : (
                `Confirmer le paiement (${totalAmount} MAD)`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
